import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { BubbleBackground } from '../components/BubbleBackground';
import { useBubbleEffect } from '../components/BubbleClick';
import { WaveSeparator } from '../components/WaveSeparator';
import { BeforeAfterGallery } from '../components/BeforeAfterGallery';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

// Gallery images organized by room type
const galleryCategories = [
  {
    name: "Kitchens",
    images: [
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/zilpezpw_3271196012170840086.jpeg", title: "Kitchen with Flowers" },
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/sh4kjh5v_7607940655992651147.jpeg", title: "Kitchen Cleaning" },
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/5hoa8td6_1541734077259078628.jpeg", title: "White Kitchen" },
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/p86b1ibf_2242216101989904875.jpeg", title: "Cherry Kitchen" },
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/3gry2z5m_3569955351552978925.jpeg", title: "Modern Kitchen" },
    ]
  },
  {
    name: "Living Rooms",
    images: [
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/yclah6um_5748846733002107081.jpeg", title: "Living Room" },
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/4ahxulej_3072566328646840743.jpeg", title: "Elegant Living Room" },
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/0gp9yxb3_4696354046434858665.jpeg", title: "Open Floor Plan" },
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/e5z3bzr1_6862006522616167584.jpeg", title: "Hardwood Floors" },
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/uxb4gcf4_20260424_133407.jpeg", title: "Dining Room Cleaning" },
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/suebl16m_20260424_134101.jpeg", title: "Cozy Living Room" },
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/98y42sln_20260424_134030.jpeg", title: "Polished Dining Room" },
    ]
  },
  {
    name: "Staircases & Entryways",
    images: [
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/78teniu8_4874626381787809346.jpeg", title: "Grand Staircase" },
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/bwkiwl0q_7962268743797319950.jpeg", title: "Carpeted Staircase" },
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/c3fbjcvr_904505243482204448.jpeg", title: "Chandelier Detail" },
    ]
  },
  {
    name: "Bathrooms",
    images: [
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/g6sq648e_5622193362639120542.jpeg", title: "Bathroom & Tub" },
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/3opuybmp_849670101801722301.jpeg", title: "Clean Vanity" },
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/valxabkm_7421727686695433658.jpeg", title: "Bathroom Deep Clean" },
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/g9j3p9vu_20260511_094129.jpeg", title: "Master Bathroom" },
    ]
  },
  {
    name: "Move-Out Ready",
    images: [
      { src: "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/zeb6rior_3337144130066025759.jpeg", title: "Empty Bedroom" },
    ]
  },
];

export const GalleryPage = () => {
  const { triggerBubbles, BubbleEffects } = useBubbleEffect();

  const handleOpenQuote = (e) => {
    triggerBubbles(e);
    setTimeout(() => {
      window.location.href = '/#contact';
    }, 300);
  };

  return (
    <div className="min-h-screen bg-white relative">
      <BubbleBackground />
      <BubbleEffects />
      <Navbar onOpenQuote={handleOpenQuote} />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-sky-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-outfit font-bold text-slate-800 mb-4">
              Our <span className="text-sky-600">Work</span> Gallery
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Real results from homes and businesses across Kansas City. See the difference our professional cleaning makes.
            </p>
          </motion.div>
        </div>
        <WaveSeparator fillColor="#ffffff" />
      </section>

      {/* Photo Gallery Grid - Organized by Category */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {galleryCategories.map((category, catIndex) => (
            <div key={category.name} className="mb-16">
              <motion.h2
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="text-2xl md:text-3xl font-outfit font-bold text-slate-800 mb-6"
              >
                <span className="text-pink-500">{category.name}</span>
              </motion.h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {category.images.map((image, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="group relative aspect-square rounded-xl overflow-hidden shadow-lg cursor-pointer"
                    data-testid={`gallery-${category.name.toLowerCase()}-${index}`}
                  >
                    <img
                      src={image.src}
                      alt={image.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent 
                                    opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <p className="text-white font-semibold">{image.title}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Before & After Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-outfit font-bold text-slate-800 mb-4">
              Before & <span className="text-pink-500">After</span>
            </h2>
            <p className="text-lg text-slate-600">
              Drag the slider to see the transformation
            </p>
          </motion.div>
          
          <BeforeAfterGallery />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-sky-500 to-pink-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-outfit font-bold text-white mb-4">
            Ready for Results Like These?
          </h2>
          <p className="text-white/90 mb-6">
            Get your free quote today and join our satisfied customers.
          </p>
          <Button
            onClick={handleOpenQuote}
            className="rounded-full bg-white text-sky-600 hover:bg-slate-100 px-8 py-6 text-lg font-medium"
            data-testid="gallery-cta-quote"
          >
            Get Free Quote
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-400">
            &copy; {new Date().getFullYear()} Your Family House Cleaning LLC. All rights reserved.
          </p>
          <Link to="/" className="text-sky-400 hover:text-sky-300 mt-2 inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default GalleryPage;
